import { SubAPI } from "./SubAPI";

export type ProjectFilter = {
    search?: string;

    // narrows the list to one team. Without it the list holds every team the user is on.
    team?: number;

    // narrows the list to one project, for watching a single project live
    id?: number;

    // leaving this off lists every project, archived or not
    archived?: boolean;

    // the window dueInRange counts against, "2006-01-02" and inclusive. Without it
    // dueInRange is 0.
    dueFrom?: string;
    dueTo?: string;

    // the reader's own date, "2006-01-02". A task carried onto it counts against the
    // window holding today rather than the one it was due in.
    today?: string;
}

export class Projects extends SubAPI {
    list(props: ProjectFilter) {
        return this.fetcher.get<Project[]>("/api/projects", props);
    }

    get(props: { id: number }) {
        return this.fetcher.get<Project>("/api/project", props);
    }

    upsert(props: ProjectInput) {
        return this.fetcher.post<Project>("/api/project", props);
    }

    getGoal(props: { id: number }) {
        return this.fetcher.get<Goal>("/api/goal", props);
    }

    // archiving a goal archives the open tasks working towards it
    upsertGoal(props: GoalInput) {
        return this.fetcher.post<Goal>("/api/goal", props);
    }

    // move puts the project, its goals and all of its tasks on another team
    move(props: { id: number; team: number }) {
        return this.fetcher.post<ProjectMoved>("/api/project/move", props);
    }
}

export type Project = {
    id: number;
    name: string;
    description: string;

    // a key into the palette the server serves, see ProjectPalette
    color: string;

    // 2 characters, A-Z or 0-9, unique on the team. Left blank on create, the server
    // makes one from the name.
    code: string;

    // who can see the project. It's set on create; after that only move changes it.
    // teamName is read-only.
    team: number;
    teamName: string;

    archived: boolean;
    createdAt: string;
    createdBy: number;
    updatedAt: string;

    // dueInRange counts the open tasks due inside the window the list was asked for,
    // and openCount every open task in the project. Both are read-only.
    dueInRange: number;
    openCount: number;

    // the pieces this project is broken into. Edited with upsertGoal, not through
    // this field.
    goals: Goal[];
}

// ProjectColor is one entry in the palette the feed serves (see api/Feed). The palette
// belongs to the server so the app and the digest email draw a project the same way.
// Every tint is light enough to carry the palette's text colour, so a coloured tile
// reads the same as an uncoloured one.
export type ProjectColor = {
    key: string;
    label: string;

    // bg fills the tile or chip, hover is the same tint one step darker, and border
    // is the saturated version used for the swatch outline
    bg: string;
    hover: string;
    border: string;

    // code is the text colour of the project code (see ProjectCode)
    code: string;
}

export type ProjectPalette = {
    colors: ProjectColor[];

    // neutral dresses whatever isn't filed under a project
    neutral: ProjectColor;

    // text is the body colour every tint is chosen to carry
    text: string;
}

export type ProjectInput = Partial<Omit<Project, "dueInRange" | "openCount" | "goals">>;

export type ProjectMoved = {
    moved: number;

    // the assignees removed because they aren't on the new team
    unassigned: number;
}

// Goal is a named piece of a project. A task works towards a project directly or
// towards one of its goals. A goal is never completed, only archived.
export type Goal = {
    id: number;
    project: number;
    name: string;
    description: string;
    sortOrder: number;
    archived: boolean;
    createdAt: string;
    createdBy: number;
    updatedAt: string;

    // dueInRange and openCount count the same way the project's do
    dueInRange: number;
    openCount: number;
}

export type GoalInput = Partial<Omit<Goal, "dueInRange" | "openCount">>;
